<template>
  <a-modal
    :open="visible"
    title="版本历史"
    :footer="null"
    width="700px"
    @cancel="handleClose"
  >
    <a-spin :spinning="loading">
      <div class="version-history">
        <a-timeline v-if="versions.length > 0">
          <a-timeline-item
            v-for="version in versions"
            :key="version.id"
            :color="version.id === versions[0]?.id ? 'green' : 'blue'"
          >
            <div class="version-item">
              <div class="version-header">
                <a-tag :color="version.id === versions[0]?.id ? 'green' : 'blue'">
                  {{ version.version }}
                </a-tag>
                <span class="version-time">{{ formatDate(version.created_at) }}</span>
                <span class="version-operator">{{ version.operator_name || '系统' }}</span>
              </div>
              <div class="version-summary">
                {{ version.change_summary }}
              </div>
              <div v-if="version.change_description" class="version-description">
                变更说明: {{ version.change_description }}
              </div>
              <a-button
                type="link"
                size="small"
                @click="handleViewSnapshot(version)"
              >
                查看该版本快照
              </a-button>
            </div>
          </a-timeline-item>
        </a-timeline>

        <a-empty
          v-else
          description="暂无版本记录"
        >
          <template #image>
            <HistoryOutlined style="font-size: 48px; color: #ccc;" />
          </template>
        </a-empty>
      </div>
    </a-spin>

    <!-- 版本快照详情弹窗 -->
    <a-modal
      v-model:open="showSnapshot"
      :title="`版本快照 - ${selectedVersion?.version}`"
      width="800px"
      :footer="null"
    >
      <div class="snapshot-content">
        <pre class="snapshot-json"><code v-html="highlightJson(snapshotData)"></code></pre>
      </div>
    </a-modal>
  </a-modal>
</template>

<script setup>
import { ref, watch } from 'vue';
import { message } from 'ant-design-vue';
import { HistoryOutlined } from '@ant-design/icons-vue';
import axios from 'axios';

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  schemeId: {
    type: Number,
    required: true
  }
});

const emit = defineEmits(['update:visible']);

const loading = ref(false);
const versions = ref([]);
const showSnapshot = ref(false);
const selectedVersion = ref(null);
const snapshotData = ref(null);

// 获取版本历史
const fetchVersionHistory = async () => {
  if (!props.schemeId) return;
  
  loading.value = true;
  try {
    const res = await axios.get(`/api/call-scheme/${props.schemeId}/output-params/versions`);
    if (res.data.code === 200) {
      versions.value = res.data.data.versions || [];
    } else {
      message.error(res.data.message || '获取版本历史失败');
    }
  } catch (error) {
    console.error('获取版本历史失败:', error);
    message.error('获取版本历史失败');
  } finally {
    loading.value = false;
  }
};

// 查看版本快照
const handleViewSnapshot = async (version) => {
  selectedVersion.value = version;
  try {
    const res = await axios.get(`/api/output-params/version/${version.id}`);
    if (res.data.code === 200) {
      snapshotData.value = res.data.data.snapshot;
      showSnapshot.value = true;
    } else {
      message.error(res.data.message || '获取版本快照失败');
    }
  } catch (error) {
    console.error('获取版本快照失败:', error);
    message.error('获取版本快照失败');
  }
};

// JSON语法高亮
const highlightJson = (obj) => {
  if (!obj) return '';
  const jsonStr = JSON.stringify(obj, null, 2);
  return jsonStr
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"([^"]+)":/g, '<span class="json-key">"$1"</span>:')
    .replace(/: "([^"]*)"/g, ': <span class="json-string">"$1"</span>')
    .replace(/: (\d+)/g, ': <span class="json-number">$1</span>')
    .replace(/: (true|false)/g, ': <span class="json-boolean">$1</span>')
    .replace(/: (null)/g, ': <span class="json-null">$1</span>');
};

// 格式化日期
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// 关闭弹窗
const handleClose = () => {
  emit('update:visible', false);
};

// 监听弹窗显示状态
watch(() => props.visible, (val) => {
  if (val) {
    fetchVersionHistory();
  }
});
</script>

<style scoped>
.version-history {
  max-height: 500px;
  overflow-y: auto;
  padding: 16px;
}

.version-item {
  padding-bottom: 8px;
}

.version-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.version-time {
  color: #666;
  font-size: 13px;
}

.version-operator {
  color: #999;
  font-size: 12px;
}

.version-summary {
  color: #333;
  font-size: 14px;
  margin-bottom: 4px;
}

.version-description {
  color: #666;
  font-size: 13px;
  margin-bottom: 4px;
  padding: 8px 12px;
  background: #f5f5f5;
  border-radius: 4px;
}

.snapshot-content {
  max-height: 500px;
  overflow: auto;
  background: #1e1e1e;
  border-radius: 8px;
}

.snapshot-json {
  margin: 0;
  padding: 16px;
  font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 12px;
  line-height: 1.5;
  color: #d4d4d4;
}

:deep(.json-key) {
  color: #9cdcfe;
}

:deep(.json-string) {
  color: #ce9178;
}

:deep(.json-number) {
  color: #b5cea8;
}

:deep(.json-boolean) {
  color: #569cd6;
}

:deep(.json-null) {
  color: #808080;
}
</style>

